import React, { useMemo, useState, useEffect } from 'react'
import { Box, Flex, useToast, Text, Button, VStack } from '@chakra-ui/react'
import {
  DialogContainer,
  DialogActions,
  DialogHeader,
  DialogBody
} from '@handlers/sessions/components/dialog'
import { copyToClipboard } from '@utilities'
import { useQuery, useQueryClient, useMutation } from 'react-query'
import { endpoints } from '@api'
import _ from 'lodash'
import moment from 'moment'
import { useStoreState } from 'easy-peasy'
import { TrashIcon } from '@icons'
import axios from 'axios'
import { RadioButtonLoading } from '@components/icons'
import flagsmith from 'flagsmith'
import { FlagsmithFeatures } from '@constants/flagsmith'

const formatTime = seconds => {
  const duration = moment.duration(seconds, 'seconds')
  const hours = duration
    .hours()
    .toString()
    .padStart(1, '0')
  const minutes = duration
    .minutes()
    .toString()
    .padStart(2, '0')
  const secondsFormatted = duration
    .seconds()
    .toString()
    .padStart(2, '0')

  return `${hours}:${minutes}:${secondsFormatted}`
}

const DeleteConfirmation = ({ isOpen, onClose, onConfirm, isLoading }) => {
  return (
    <DialogContainer onClose={onClose} isOpen={isOpen}>
      <DialogActions onClose={onClose} />
      <DialogHeader text="Are you sure?" />
      <DialogBody>
        <VStack spacing={6}>
          <Text textAlign="center">
            This session's transcript will be deleted. This cannot be undone.
          </Text>
          <VStack w="100%">
            <Button
              bg="error"
              isFullWidth
              size="lg"
              onClick={onConfirm}
              isLoading={isLoading}
            >
              Delete transcript
            </Button>
            <Button
              _focus={{ outline: 'none' }}
              variant="outline"
              isFullWidth
              size="lg"
              onClick={onClose}
            >
              Cancel
            </Button>
          </VStack>
        </VStack>
      </DialogBody>
    </DialogContainer>
  )
}

const formatTranscript = utterances => {
  const parts = []
  let currentObject = null

  for (const utterance of utterances) {
    if (currentObject && utterance.speaker === currentObject.speaker) {
      currentObject.transcript += ` ${utterance.transcript}`
    } else {
      if (currentObject !== null) {
        parts.push(currentObject)
      }

      currentObject = {
        speaker: utterance.speaker,
        transcript: utterance.transcript,
        start: formatTime(utterance.start)
      }
    }
  }

  if (currentObject !== null) {
    parts.push(currentObject)
  }

  return parts
}

const Transcript = ({ sessionId }) => {
  const [rawTranscript, setRawTranscript] = useState('')
  const [transcriptError, setTranscriptError] = useState('')
  const [loadingTranscript, setLoadingTranscript] = useState(false)
  const [transcript, setTranscript] = useState([])
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(
    false
  )
  const { height: paywallBannerHeight } = useStoreState(
    state => state.paywallBanner
  )
  const queryClient = useQueryClient()
  const toast = useToast()
  const showTranscriptCopy = flagsmith.hasFeature(
    FlagsmithFeatures.TRANSCRIPT_COPY
  )

  const {
    data: sessionTranscriptData,
    isLoading: isLoadingTranscript
  } = useQuery(
    [endpoints.getSessionTranscript.getCacheId(), sessionId],
    () => endpoints.getSessionTranscript.request({ sessionId }),
    {
      refetchInterval: data => {
        if (data?.sessionTranscript?.isLoading) {
          return 5000
        }

        return false
      },
      onSuccess: ({ sessionTranscript }) => {
        if (!sessionTranscript || sessionTranscript.isLoading) {
          return
        }

        const { url } = sessionTranscript

        setLoadingTranscript(true)
        axios
          .get(url)
          .then(res => {
            setRawTranscript(
              res?.data?.results?.channels?.[0]?.alternatives?.[0]
                ?.transcript || ''
            )
            setTranscript(
              formatTranscript(res?.data?.results?.utterances || [])
            )
            setLoadingTranscript(false)
          })
          .catch(err => {
            if (err?.response?.status === 404) {
              setTranscriptError('Transcript no longer available')
            } else {
              setTranscriptError('Error fetching transcript')
            }
            setLoadingTranscript(false)
          })
      }
    }
  )

  const {
    mutate: deleteSessionTranscript,
    isLoading: isDeletingTranscript
  } = useMutation(endpoints.deleteSessionTranscript.request, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        endpoints.getSessionTranscript.getCacheId(),
        sessionId
      ])
      queryClient.setQueryData(
        [endpoints.getSessionTranscript.getCacheId(), sessionId],
        oldData => ({
          sessionTranscript: null
        })
      )
      setRawTranscript('')
      setTranscript([])
      toast({
        description: 'Transcript deleted!',
        status: 'success',
        isClosable: true,
        duration: 2000
      })
    }
  })

  const renderTranscript = () => {
    return transcript.map((item, index) => {
      const { transcript, speaker, start } = item
      return (
        <Flex key={index} gap="8px">
          <Flex gap="4px">
            <Text minWidth="65px" color="medium_gray">
              {start}
            </Text>
            <Text minWidth="100px">Speaker #{speaker + 1}</Text>
          </Flex>
          <Flex flexGrow="1">
            <Text>{transcript}</Text>
          </Flex>
        </Flex>
      )
    })
  }

  const handleCopyRawTranscript = () => {
    copyToClipboard(rawTranscript)
  }

  const sessionTranscript = sessionTranscriptData?.sessionTranscript

  if (sessionTranscript?.isLoading || isLoadingTranscript) {
    return (
      <Flex
        p="48px 32px"
        flexDirection="column"
        border="1px solid"
        borderColor="pale_gray"
        borderRadius="8px"
        mt="24px"
      >
        <Text textAlign="center" fontWeight="bold" mb="xxsmall">
          {`Transcribing audio...`}
        </Text>
        <Text textAlign="center" color="#757575">
          This process could take up to 3 minutes
        </Text>
        <Box h="16px" />
        <Flex flexDirection="column" alignSelf="center" gap="16px">
          <RadioButtonLoading />
        </Flex>
      </Flex>
    )
  }

  if (loadingTranscript) {
    return null
  }

  if (!sessionTranscript) {
    return (
      <Flex
        p="16px"
        border="1px solid"
        height="200px"
        borderColor="pale_gray"
        borderRadius="8px"
        justifyContent="center"
        alignItems="center"
        color="#757575"
        mt="24px"
      >
        <Text>No transcript available</Text>
      </Flex>
    )
  }

  return (
    <>
      <Flex
        flexDirection="column"
        position="sticky"
        bg="white"
        zIndex="999"
        paddingBottom="16px"
        top={`${153 + paywallBannerHeight}px`}
      >
        <Box display="flex" justifyContent="space-between" mt="16px">
          <Text display="flex" alignItems="center" fontWeight="bold">
            Transcript
          </Text>
          <Button
            variant="ghost"
            borderRadius="24px"
            p="0px"
            _hover={{ opacity: 0.7 }}
            _focus={{ outline: 'none' }}
            onClick={() => setIsDeleteConfirmationOpen(true)}
          >
            <TrashIcon fill="#EB5164" width="16" height="16" />
            <Box width="4px" />
            Delete
          </Button>
          {showTranscriptCopy && (
            <Button onClick={handleCopyRawTranscript}>
              Copy Raw Transcript
            </Button>
          )}
        </Box>
      </Flex>
      <Flex flexDirection="column" gap="16px">
        {renderTranscript()}
      </Flex>
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        isLoading={isDeletingTranscript}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onConfirm={() => deleteSessionTranscript({ id: sessionTranscript.id })}
      />
    </>
  )
}

export default Transcript
